import { useState, type FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ordersApi } from '../api/orders';
import { toErrorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Card, PageHead } from '../components/ui';
import { Empty, ErrorState, Loading } from '../components/DataStates';
import { Modal } from '../components/Modal';
import { Receipt } from '../components/Receipt';
import { money } from '../lib/format';

export function BillDetailPage() {
  const { id } = useParams();
  const orderId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isSuperAdmin, user } = useAuth();
  const isAdmin = isSuperAdmin || user?.role === 'STORE_ADMIN';

  const [cancelling, setCancelling] = useState(false);
  const [refunding, setRefunding] = useState(false);

  const q = useQuery({
    queryKey: ['bill', orderId],
    queryFn: () => ordersApi.get(orderId),
    enabled: Number.isFinite(orderId),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['bill', orderId] });
    qc.invalidateQueries({ queryKey: ['bills'] });
  };

  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorState message={toErrorMessage(q.error)} onRetry={q.refetch} />;
  if (!q.data) return <Empty title="Bill not found" />;

  const order = q.data;

  return (
    <>
      <PageHead
        title={`Bill ${order.billNumber}`}
        subtitle={order.storeName}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => navigate(-1)}>← Back</button>
            <button className="btn btn-primary" onClick={() => window.print()}>🖨️ Print / Reprint</button>
            {order.status === 'COMPLETED' && (
              <button className="btn btn-secondary" onClick={() => setCancelling(true)}>Cancel Bill</button>
            )}
            {isAdmin && (
              <button className="btn btn-secondary" onClick={() => setRefunding(true)}>Record Refund</button>
            )}
          </>
        }
      />

      <div className="row gap-md flex-wrap" style={{ alignItems: 'flex-start' }}>
        <Card title="Receipt Preview" className="receipt-card">
          <Receipt order={order} />
        </Card>

        <Card title="Bill Summary" className="grow">
          <div className="table-wrap">
            <table className="data">
              <tbody>
                <tr><td>Status</td><td className="text-right">{order.status}</td></tr>
                <tr><td>Cashier</td><td className="text-right">{order.cashierName}</td></tr>
                <tr><td>Order Type</td><td className="text-right">{order.orderType}</td></tr>
                <tr><td>Subtotal</td><td className="text-right">{money(order.subtotal)}</td></tr>
                <tr><td>Discount</td><td className="text-right">- {money(order.discountAmount)}</td></tr>
                <tr><td>Taxable</td><td className="text-right">{money(order.taxableAmount)}</td></tr>
                <tr><td>CGST ({order.cgstRate}%)</td><td className="text-right">{money(order.cgstAmount)}</td></tr>
                <tr><td>SGST ({order.sgstRate}%)</td><td className="text-right">{money(order.sgstAmount)}</td></tr>
                <tr><td><strong>Grand Total</strong></td><td className="text-right"><strong>{money(order.grandTotal)}</strong></td></tr>
                <tr><td>Payment</td><td className="text-right">{order.paymentMethod}</td></tr>
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {cancelling && (
        <ReasonModal
          title="Cancel Bill"
          label="Cancellation reason"
          confirmLabel="Cancel Bill"
          onClose={() => setCancelling(false)}
          onConfirm={(reason) => ordersApi.cancel(orderId, { reason })}
          onDone={() => { setCancelling(false); invalidate(); }}
        />
      )}

      {refunding && (
        <RefundModal
          max={order.grandTotal}
          onClose={() => setRefunding(false)}
          onConfirm={(amount, reason) => ordersApi.refund(orderId, { amount, reason })}
          onDone={() => { setRefunding(false); invalidate(); }}
        />
      )}
    </>
  );
}

function ReasonModal({
  title, label, confirmLabel, onClose, onConfirm, onDone,
}: {
  title: string; label: string; confirmLabel: string;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<unknown>;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({ mutationFn: () => onConfirm(reason), onSuccess: onDone, onError: (e) => setError(toErrorMessage(e)) });

  return (
    <Modal open title={title} onClose={onClose} footer={
      <>
        <button className="btn btn-ghost" onClick={onClose}>Close</button>
        <button className="btn btn-primary" disabled={!reason || m.isPending} onClick={() => { setError(null); m.mutate(); }}>
          {m.isPending ? 'Saving…' : confirmLabel}
        </button>
      </>
    }>
      {error && <div className="form-error">{error}</div>}
      <div className="field">
        <label>{label} *</label>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </div>
    </Modal>
  );
}

function RefundModal({
  max, onClose, onConfirm, onDone,
}: {
  max: number;
  onClose: () => void;
  onConfirm: (amount: number, reason: string) => Promise<unknown>;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => onConfirm(Number(amount), reason),
    onSuccess: onDone,
    onError: (e) => setError(toErrorMessage(e)),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    m.mutate();
  }

  return (
    <Modal open title="Record Refund" onClose={onClose} footer={
      <>
        <button className="btn btn-ghost" onClick={onClose}>Close</button>
        <button className="btn btn-primary" disabled={!amount || Number(amount) <= 0 || m.isPending} onClick={submit}>
          {m.isPending ? 'Saving…' : 'Record Refund'}
        </button>
      </>
    }>
      <form onSubmit={submit}>
        {error && <div className="form-error">{error}</div>}
        <div className="field">
          <label>Amount (max {money(max)}) *</label>
          <input type="number" step="0.01" min="0" max={max} value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </div>
        <div className="field">
          <label>Reason</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
      </form>
    </Modal>
  );
}
